import * as React from 'react'
import { FileType } from 'interfaces'
import { useIntl } from 'react-intl'
import { bytesToSize } from 'shared'
import { getFileTypeByMimeType, getFileMaxSizeByType, getAllowedFormatsByFileType } from 'utils/file'

const BASE_TEN = 10

export interface FileValidatorProps {
  file: File
  /** max file size in bytes */
  maxSize?: number
  allowedFileFormats?: string[]
  children?: React.ReactElement<any>
  disabled?: boolean
  onValidationSuccess: () => void
  onValidationError: (error: string) => void
}

export const FileValidator = React.memo(function FileValidator(props: FileValidatorProps) {
  const intl = useIntl()
  const file = props.file
  const type = getFileTypeByMimeType(file.type)
  const defaultFileFormats = type && getAllowedFormatsByFileType(type)
  const defaultMaxSize = type && getFileMaxSizeByType(type)
  const allowedFileFormats = props.allowedFileFormats || defaultFileFormats
  const allowedMaxSize = props.maxSize || defaultMaxSize

  React.useEffect(() => {
    if (props.disabled) {
      props.onValidationSuccess()
      return
    }

    if (allowedMaxSize && file.size > allowedMaxSize) {
      const maxSizeFormatted = bytesToSize(allowedMaxSize)
      const maxSizePrettyString = `${parseInt(maxSizeFormatted, BASE_TEN)} ${maxSizeFormatted.split(' ')[1]}`

      if (type === FileType.Image || type === FileType.Gif) {
        const imageErrorMessage = intl.formatMessage({ id: 'file.messages.photo-size-exceeded' }, { size: maxSizePrettyString })
        props.onValidationError(imageErrorMessage)
        return
      }

      if (type === FileType.Video) {
        const videoErrorMessage = intl.formatMessage({ id: 'file.messages.video-size-exceeded' }, { size: maxSizePrettyString })
        props.onValidationError(videoErrorMessage)
        return
      }

      props.onValidationError(`Your file is bigger than the maximum allowed of ${maxSizePrettyString}`)
      return
    }

    if (allowedFileFormats && !allowedFileFormats.includes(file.type)) {
      props.onValidationError(intl.formatMessage({ id: 'file.messages.invalid-type' }))
      return
    }

    props.onValidationSuccess()
    // EXPL: re-validate when the file or its constraints change. The flagged `type`,
    // `allowedMaxSize`, `allowedFileFormats`, `file.size`/`file.type` are all derived from
    // these deps; `intl` and the validation callbacks are intentionally excluded so validation
    // doesn't re-run (and re-invoke onValidationSuccess/Error) on every render or locale change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.file, props.maxSize, props.allowedFileFormats, props.disabled])

  return <React.Fragment>{props.children}</React.Fragment>
})

export default FileValidator
